import { Injectable } from "@angular/core";
import { IAgreementPreviewResponse } from "@bca-agreement/models/agreement-preview-response";
import { IIssuerResponse } from "@bca-agreement/models/issuer-response";
import * as agreementActions from "@bca-agreement/store/agreement.actions";
import { AgreementActionTypes } from "@bca-agreement/store/agreement.actions";
import { ApiService } from "@bca-core/services/api.service";
import { MessageType } from "@bca-shared/models/_enums";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction, createActions } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { EMPTY } from "rxjs";
import {
  catchError,
  filter,
  mergeMap,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";

@Injectable()
export class AgreementEffects {
  fetchIssuers = createEffect(() =>
    this._actions.pipe(
      ofType(AgreementActionTypes.FetchIssuers),
      withLatestFrom(this._store$.pipe(select(({ agreement }) => agreement))),
      switchMap(() => {
        this._store$.dispatch(
          this._createAction(
            new agreementActions.IssuersLoading({ loading: true })
          )
        );
        return this._apiService.getIdealIssuers().pipe(
          catchError((error) => {
            const message = {
              body: "NOTIFICATION.ERROR.ISSUERS",
              type: MessageType.ERROR,
              context: { error },
              originalException: error,
            };
            this._store$.dispatch(
              this._createAction(new appActions.SetMessage(message))
            );
            return EMPTY;
          })
        );
      }),
      filter((response: IIssuerResponse) => response.success),
      mergeMap((response: IIssuerResponse) => {
        const { idealIssuers } = response;
        return this._createActions([
          new agreementActions.SetIssuers(idealIssuers),
          new agreementActions.IssuersLoading({ loading: false }),
        ]);
      })
    )
  );

  fetchAgreementPreview = createEffect(() =>
    this._actions.pipe(
      ofType(AgreementActionTypes.FetchAgreementPreview),
      withLatestFrom(this._store$.pipe(select(({ receipt }) => receipt))),
      switchMap(([, receipt]) => {
        this._store$.dispatch(
          this._createAction(
            new agreementActions.AgreementPreviewLoading({ loading: true })
          )
        );
        return this._apiService.getAgreementPreview(receipt).pipe(
          catchError((error) => {
            const message = {
              body: "NOTIFICATION.ERROR.AGREEMENT_PREVIEW",
              type: MessageType.ERROR,
              context: { error },
              originalException: error,
            };
            this._store$.dispatch(
              this._createAction(new appActions.SetMessage(message))
            );
            return EMPTY;
          })
        );
      }),
      filter((response: IAgreementPreviewResponse) => response.success),
      mergeMap((response: IAgreementPreviewResponse) => {
        const { agreementPreview: agreementPreviewAsBase64 } = response;
        return this._createActions([
          new agreementActions.SetAgreementPreview({
            agreementPreviewAsBase64,
          }),
          new agreementActions.AgreementPreviewLoading({ loading: false }),
        ]);
      })
    )
  );

  private _createAction(action: IAction): IAction {
    return createAction(action, AgreementEffects);
  }

  private _createActions(actions: IAction[]): IAction[] {
    return createActions(actions, AgreementEffects);
  }

  constructor(
    private _actions: Actions,
    private _store$: Store<IRootState>,
    private _apiService: ApiService
  ) {}
}
